import React, { useState, useContext } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { ThemeContext } from "../context/ThemeContext";
import { uploadFile, downloadFile } from "../api/fileService";
import FileDropZone from "../components/FileDropZone";
import UploadToast from "../components/UploadToast";
import ExpiryOptions from "../components/ExpiryOptions";
import LastUploadedFile from "../components/LastUploadedFile";
import UploadLoader from "../components/UploadLoader";
import SavedFilesList from "../components/SavedFilesList";
import { useAuth } from "react-oidc-context";
import { useSavedFiles } from "../hooks/useSavedFiles";
import { copyToClipboard } from "../utils/Common";

const LandingPage = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const auth = useAuth();

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [lastUploadedFileName, setLastUploadedFileName] = useState("");
  const [expiresIn, setExpiresIn] = useState(10);
  const [noExpiry, setNoExpiry] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { savedFiles, loadingFiles, setSavedFiles } = useSavedFiles(auth);
  const FILES_CACHE_KEY = "savedFilesCache";

  const uploadAndShare = async (selectedFile) => {
    setUploading(true);
    try {
      const response = await uploadFile(selectedFile, auth, expiresIn, noExpiry);
      const link = response.downloadUrl;
      setShareLink(link);
      setLastUploadedFileName(selectedFile.name);
      await copyToClipboard(link, setToastMessage, expiresIn);
      setFile(null);

      if (noExpiry) {
        setSavedFiles((prev) => [
          {
            fileName: selectedFile.name,
            uploadedAt: new Date().toISOString(),
            shareId: response.shareId,
          },
          ...prev,
        ]);

        sessionStorage.setItem(
          FILES_CACHE_KEY,
          JSON.stringify({ savedFiles, timestamp: Date.now() })
        );
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setToastMessage("Upload failed. Please try again.");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAndShare = async (shareId, isDownload) => {
    setActiveActionId(shareId);
    setIsProcessing(true);

    try {
      const response = await downloadFile(shareId, auth);

      if (isDownload) {
        window.open(response.downloadUrl, "_blank");
      } else {
        await copyToClipboard(
          response.downloadUrl,
          setToastMessage,
          response.expiresIn
        );
      }
    } catch (err) {
      console.error("Failed to process file:", err);
      setToastMessage("Failed to process request");
    } finally {
      setIsProcessing(false);
      setActiveActionId(null);
    }
  };

  return (
    <Container fluid className="py-5">
      <Row className="justify-content-center g-4">
        {/* Saved Files List */}
        <Col md={4} className="order-2 order-md-1">
          <SavedFilesList
            files={savedFiles}
            isDark={isDark}
            loading={loadingFiles}
            handleDownloadAndShare={handleDownloadAndShare}
            activeActionId={activeActionId}
            isProcessing={isProcessing}
          />
        </Col>

        {/* Upload Panel */}
        <Col md={6} className="order-1 order-md-2">
          <Card
            className="shadow-lg border-0 p-5 text-center"
            style={{
              backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
              color: isDark ? "#f1f1f1" : "#000000",
              transition: "all 0.3s ease",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }}
          >
            <div>
              <h3 className="mb-4 fw-bold">Share Your File 🚀</h3>

              <FileDropZone
                file={file}
                setFile={setFile}
                onFileSelect={uploadAndShare}
                isDark={isDark}
              />

              <ExpiryOptions
                expiresIn={expiresIn}
                setExpiresIn={setExpiresIn}
                noExpiry={noExpiry}
                setNoExpiry={setNoExpiry}
                isDark={isDark}
              />

              {uploading && (
                <UploadLoader
                  fileName={file?.name}
                  progress={70}
                  isDark={isDark}
                />
              )}

              <UploadToast
                toastMessage={toastMessage}
                setToastMessage={setToastMessage}
                isDark={isDark}
              />
            </div>
            
            {shareLink && (
              <LastUploadedFile
                fileName={lastUploadedFileName}
                shareLink={shareLink}
                onShareClick={() => copyToClipboard(shareLink)}
                isDark={isDark}
                expiresIn={expiresIn}
              />
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LandingPage;
